import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { MonthlyPensionContributionView } from '../../../../models/payroll';

interface GpssaAdpfSplitTableProps {
  contributions: MonthlyPensionContributionView[];
  /** UAE nationals enrolled in GPSSA (from coverage). Used for richer empty states. */
  gpssaEnrolledCount?: number;
  /** UAE nationals enrolled in ADPF (from coverage). Used for richer empty states. */
  adpfEnrolledCount?: number;
  /** Number of employees skipped during the latest generate run for each scheme. */
  gpssaSkippedCount?: number;
  adpfSkippedCount?: number;
}

const SchemeCard: React.FC<{
  title: string;
  scheme: 'GPSSA' | 'ADPF';
  footer: string;
  items: MonthlyPensionContributionView[];
  accentClass: string;
  enrolledCount?: number;
  skippedCount?: number;
}> = ({ title, scheme, footer, items, accentClass, enrolledCount = 0, skippedCount = 0 }) => {
  const { t } = useTranslation();
  const totalEmployee = items.reduce((s, c) => s + (c.employeeContribution ?? 0), 0);
  const totalEmployer = items.reduce((s, c) => s + (c.employerContribution ?? 0), 0);
  const totalAll = items.reduce((s, c) => s + (c.totalContribution ?? 0), 0);
  const isEmpty = items.length === 0;
  const hasEnrolledButNoContrib = isEmpty && enrolledCount > 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold ${accentClass}`}>{title}</h3>
          <span className="text-xs text-muted-foreground">
            {items.length} {t('common.employees', 'employees')}
          </span>
        </div>
        {isEmpty ? (
          hasEnrolledButNoContrib ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-amber-900">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="text-sm">
                <p className="font-medium">
                  {t(
                    'payroll.complianceCenter.pension.enrolledNoContrib',
                    '{{count}} employee(s) enrolled in {{scheme}}, but no contributions for this period.',
                    { count: enrolledCount, scheme }
                  )}
                </p>
                <p className="text-amber-800/90 mt-0.5 text-xs">
                  {skippedCount > 0
                    ? t(
                        'payroll.complianceCenter.pension.checkSkipped',
                        'The latest generation skipped {{count}} {{scheme}} employee(s). Review the warnings above and fix the underlying issue (e.g. missing basic-salary component) before regenerating.',
                        { count: skippedCount, scheme }
                      )
                    : t(
                        'payroll.complianceCenter.pension.runGenerate',
                        'Click "Generate Contributions" above to compute this month\'s contributions, or review the warnings if generation was already attempted.'
                      )}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-border bg-muted/30 p-3 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t(
                  'payroll.complianceCenter.pension.noEnrolledScheme',
                  'No employees enrolled in {{scheme}} for this company.',
                  { scheme }
                )}
              </p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-start py-2 font-medium">{t('common.employee', 'Employee')}</th>
                  <th className="text-end py-2 font-medium">{t('payroll.gpssa.employeeShare', 'Employee')}</th>
                  <th className="text-end py-2 font-medium">{t('payroll.gpssa.employerShare', 'Employer')}</th>
                  <th className="text-end py-2 font-medium">{t('payroll.gpssa.total', 'Total')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map(c => (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="py-2">{c.employeeName}</td>
                    <td className="py-2 text-end font-mono">{(c.employeeContribution ?? 0).toFixed(2)}</td>
                    <td className="py-2 text-end font-mono">{(c.employerContribution ?? 0).toFixed(2)}</td>
                    <td className="py-2 text-end font-mono font-semibold">{(c.totalContribution ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="py-2">{t('common.total', 'Total')}</td>
                  <td className="py-2 text-end font-mono">{totalEmployee.toFixed(2)}</td>
                  <td className="py-2 text-end font-mono">{totalEmployer.toFixed(2)}</td>
                  <td className="py-2 text-end font-mono">{totalAll.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground border-t border-border pt-2">{footer}</p>
        <span className="sr-only">{scheme}</span>
      </CardContent>
    </Card>
  );
};

export const GpssaAdpfSplitTable: React.FC<GpssaAdpfSplitTableProps> = ({
  contributions,
  gpssaEnrolledCount,
  adpfEnrolledCount,
  gpssaSkippedCount,
  adpfSkippedCount,
}) => {
  const { t } = useTranslation();
  const gpssa = contributions.filter(c => (c.scheme ?? '').toUpperCase() === 'GPSSA');
  const adpf = contributions.filter(c => (c.scheme ?? '').toUpperCase() === 'ADPF');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SchemeCard
        title={t('payroll.complianceCenter.pension.gpssaSummary', 'GPSSA Summary')}
        scheme="GPSSA"
        footer={t('payroll.complianceCenter.pension.gpssaFooter', 'UAE nationals (non-Abu Dhabi). GPSSA Federal Law 7/1999, amended FDL 57/2023.')}
        items={gpssa}
        accentClass="text-blue-700"
        enrolledCount={gpssaEnrolledCount}
        skippedCount={gpssaSkippedCount}
      />
      <SchemeCard
        title={t('payroll.complianceCenter.pension.adpfSummary', 'ADPF Summary')}
        scheme="ADPF"
        footer={t('payroll.complianceCenter.pension.adpfFooter', 'Abu Dhabi nationals. Abu Dhabi Pension Fund Law No. 2 of 2000.')}
        items={adpf}
        accentClass="text-purple-700"
        enrolledCount={adpfEnrolledCount}
        skippedCount={adpfSkippedCount}
      />
    </div>
  );
};
